/**
 * FamiliarisationSignOff - Sign-off content for familiarisation completion
 */

import React from 'react';
import { CheckCircle, AlertTriangle, Clock, FileText, Trash2 } from 'lucide-react';
import { processFile } from '../../../../utils/fileUpload';

const FamiliarisationSignOff = ({
  familiarisationComplete,
  crewSignature,
  assessorSignature,
  completionDate,
  crewPosition,
  assessorPosition,
  allTopicsCompleted,
  onFamiliarisationComplete,
  onCrewSignature,
  onAssessorSignature,
  onCompletionDate,
  onCrewPosition,
  onAssessorPosition,
  onOpenSignatureModal,  // NEW: callback to open signature modal
  viewMode,
  paperMode = false,
  // Paper mode props
  paperCompletionDate = '',
  onPaperCompletionDate,
  paperChecklistFile = null,
  onPaperChecklistFile,
  paperNotes = '',
  onPaperNotes
}) => {
  return (
    <div className="space-y-6">
      <div className={`${paperMode ? 'bg-amber-50' : 'bg-green-50'} rounded-lg p-6`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Familiarisation Sign-off</h3>
        <p className="text-gray-600">
          {paperMode 
            ? 'Paper familiarisation completed. Please sign below to confirm completion of the paper checklist.'
            : 'Complete the sign-off process to finalize the vessel familiarisation.'}
        </p>
      </div>

      {/* Completion Requirements */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200">
          <h4 className="text-base font-medium text-gray-900">Completion Requirements</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className={`flex items-center gap-3 ${allTopicsCompleted ? 'text-green-600' : 'text-red-600'}`}>
            {allTopicsCompleted ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
            <span>{paperMode ? 'Paper checklist completed' : 'All mandatory topics completed'}</span>
          </div>
          <div className={`flex items-center gap-3 ${familiarisationComplete ? 'text-green-600' : 'text-gray-600'}`}>
            {familiarisationComplete ? <CheckCircle size={20} /> : <Clock size={20} />}
            <span>Sign-off completed</span>
          </div>
          <div className={`flex items-center gap-3 ${crewSignature && assessorSignature ? 'text-green-600' : 'text-gray-600'}`}>
            {crewSignature && assessorSignature ? <CheckCircle size={20} /> : <Clock size={20} />}
            <span>All signatures provided</span>
          </div>
        </div>
      </div>

      {/* Paper Mode Evidence Section - Only shown in paper mode */}
      {paperMode && (
        <div className="bg-white border border-amber-200 rounded-lg">
          <div className="px-4 py-3 border-b border-amber-200 bg-amber-50">
            <h4 className="text-base font-medium text-amber-900">Paper Familiarisation Evidence</h4>
            <p className="text-xs text-amber-700 mt-1">
              Record the details from your completed paper familiarisation checklist
            </p>
          </div>
          <div className="p-4 space-y-4">
            {/* Paper Completion Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paper Checklist Completion Date <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Enter the date the paper familiarisation was actually completed
              </p>
              <input
                type="date"
                value={paperCompletionDate}
                onChange={(e) => onPaperCompletionDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm"
                disabled={viewMode}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Scanned Paper Checklist Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scanned Paper Checklist <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Upload a scan or photo of the completed paper familiarisation checklist
              </p>

              {paperChecklistFile ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-amber-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{paperChecklistFile.fileName}</p>
                        <p className="text-xs text-gray-500">
                          Uploaded {new Date(paperChecklistFile.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {!viewMode && (
                      <button
                        onClick={() => onPaperChecklistFile(null)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove file"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                !viewMode && (
                  <div className="border-2 border-dashed border-amber-300 rounded-lg p-6 text-center hover:border-amber-500 hover:bg-amber-50 transition-colors">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        try {
                          const processed = await processFile(file, {
                            maxSizeKB: 5120, // 5MB
                            compress: true
                          });
                          onPaperChecklistFile({
                            fileName: processed.metadata.fileName,
                            fileType: processed.metadata.fileType,
                            size: processed.metadata.fileSize || processed.metadata.compressedSize,
                            fileData: processed.data,
                            uploadedAt: processed.metadata.uploadedAt
                          });
                        } catch (error) {
                          alert('Error uploading file: ' + error.message);
                        }

                        // Clear the input so the same file can be re-selected if needed
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="paper-checklist-upload"
                    />
                    <label
                      htmlFor="paper-checklist-upload"
                      className="cursor-pointer"
                    >
                      <FileText className="h-10 w-10 text-amber-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload scanned checklist</p>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG, or PDF (max 5MB)</p>
                    </label>
                  </div>
                )
              )}
            </div>

            {/* Paper Process Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Any additional notes about the paper familiarisation process
              </p>
              <textarea
                value={paperNotes}
                onChange={(e) => onPaperNotes(e.target.value)}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm"
                placeholder="e.g., Familiarisation conducted during sea trials, additional training provided on navigation equipment..."
                disabled={viewMode}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sign-off Form */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200">
          <h4 className="text-base font-medium text-gray-900">Digital Signatures</h4>
        </div>
        <div className="p-4 space-y-4">
          {/* Crew Member Signature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Crew Member Signature
            </label>
            {crewSignature ? (
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                {typeof crewSignature === 'object' && crewSignature.data ? (
                  <>
                    <img
                      src={crewSignature.data}
                      alt="Crew signature"
                      className="border-b border-gray-300 pb-2 mb-2"
                      style={{ maxHeight: '80px', width: '100%', objectFit: 'contain' }}
                    />
                    <p className="text-sm text-gray-900 font-medium">{crewSignature.name}</p>
                    {crewSignature.position && (
                      <p className="text-sm text-gray-600">{crewSignature.position}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Signed: {crewSignature.timestamp ? new Date(crewSignature.timestamp).toLocaleString() : 'N/A'}
                    </p>
                  </>
                ) : (
                  // Legacy text signature
                  <div>
                    <p className="text-sm text-gray-900 font-medium">{crewSignature}</p>
                    {crewPosition && <p className="text-sm text-gray-600">{crewPosition}</p>}
                  </div>
                )}
                {!viewMode && (
                  <button
                    onClick={() => onOpenSignatureModal('crew')}
                    className="mt-3 text-sm text-cyan-600 hover:text-cyan-700 underline"
                  >
                    Re-sign
                  </button>
                )}
              </div>
            ) : (
              !viewMode && (
                <button
                  onClick={() => onOpenSignatureModal('crew')}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-cyan-500 hover:bg-cyan-50 transition-colors"
                >
                  <p className="text-sm text-gray-600">Click to sign</p>
                </button>
              )
            )}
          </div>

          {/* Assessor Signature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assessor Signature (Master/Supervisor)
            </label>
            {assessorSignature ? (
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                {typeof assessorSignature === 'object' && assessorSignature.data ? (
                  <>
                    <img
                      src={assessorSignature.data}
                      alt="Assessor signature"
                      className="border-b border-gray-300 pb-2 mb-2"
                      style={{ maxHeight: '80px', width: '100%', objectFit: 'contain' }}
                    />
                    <p className="text-sm text-gray-900 font-medium">{assessorSignature.name}</p>
                    {assessorSignature.position && (
                      <p className="text-sm text-gray-600">{assessorSignature.position}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Signed: {assessorSignature.timestamp ? new Date(assessorSignature.timestamp).toLocaleString() : 'N/A'}
                    </p>
                  </>
                ) : (
                  // Legacy text signature
                  <div>
                    <p className="text-sm text-gray-900 font-medium">{assessorSignature}</p>
                    {assessorPosition && <p className="text-sm text-gray-600">{assessorPosition}</p>}
                  </div>
                )}
                {!viewMode && (
                  <button
                    onClick={() => onOpenSignatureModal('assessor')}
                    className="mt-3 text-sm text-cyan-600 hover:text-cyan-700 underline"
                  >
                    Re-sign
                  </button>
                )}
              </div>
            ) : (
              !viewMode && (
                <button
                  onClick={() => onOpenSignatureModal('assessor')}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-cyan-500 hover:bg-cyan-50 transition-colors"
                >
                  <p className="text-sm text-gray-600">Click to sign</p>
                </button>
              )
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Completion Date
            </label>
            <input
              type="date"
              value={completionDate}
              onChange={(e) => onCompletionDate(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
              disabled={viewMode}
            />
          </div>

          {!viewMode && (
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={familiarisationComplete}
                  onChange={(e) => onFamiliarisationComplete(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span className="ml-3 text-sm text-gray-900">
                  I certify that the above named crew member has been familiarised with this vessel in accordance with UK Workboat Code Edition 3, Appendix 8, Sections 7.1 and 7.2. All mandatory sections have been completed, including the minimum requirements of Section 7.2 and additional task-specific training as required by Section 7.1. The crew member demonstrates understanding of vessel-specific systems, procedures, and safety requirements appropriate to their assigned duties.
                </span>
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FamiliarisationSignOff;